/**
 * TasteMatchExplanation
 *
 * Modal explaining TasteMatch™ - the compatibility score between members.
 * Explains how PersonalTaste™ and TasteDNA™ are used to find matches.
 */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Image,
  Divider,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Sparkles, Utensils, ClipboardList, Users, Dna } from 'lucide-react'
import { getCardCompatibilityIcon, getCompatibilityColor } from '@/utils/restaurantUtils'

const MotionBox = motion.create(Box)

interface TasteMatchExplanationProps {
  isOpen: boolean
  onClose: () => void
  /** Raw compatibility score from backend (-1.0 to 1.0) */
  score?: number | null
  /** Name of the other member */
  memberName?: string
}

interface ConceptCard {
  icon: React.ReactNode
  title: string
  trademark?: string
  description: string
  color: string
}

const concepts: ConceptCard[] = [
  {
    icon: <Dna size={20} />,
    title: 'TasteDNA',
    trademark: '™',
    description: 'Your unique taste fingerprint built from private restaurant ratings, menu item preferences, and quiz answers.',
    color: 'purple.500',
  },
  {
    icon: <Sparkles size={20} />,
    title: 'PersonalTaste',
    trademark: '™',
    description: 'Your Paire profile powered by your TasteDNA™. It evolves as you privately rate more restaurants and complete quizzes.',
    color: 'pink.500',
  },
  {
    icon: <Users size={20} />,
    title: 'TasteMatch',
    trademark: '™',
    description: 'The TasteMatch™ between two members based on their PersonalTaste™ Profiles.',
    color: 'blue.500',
  },
]

const tasteDNAComponents = [
  {
    icon: <Utensils size={16} />,
    label: 'Restaurant Ratings',
    description: 'How you rate the restaurants you visit',
  },
  {
    icon: <ClipboardList size={16} />,
    label: 'Menu Item Preferences',
    description: 'The dishes you love (or avoid)',
  },
  {
    icon: <Sparkles size={16} />,
    label: 'Quiz Answers',
    description: 'Your responses to taste profile quizzes',
  },
]

export function TasteMatchExplanation({
  isOpen,
  onClose,
  score,
  memberName,
}: TasteMatchExplanationProps) {
  // Convert backend score (-1 to 1) to internal rating scale (-100 to 100)
  const internalRating = score != null ? score * 100 : 0

  // Create mock restaurant for emoji/color utils
  const mockRestaurant = {
    rating: internalRating,
    isBookmarked: false,
    googleRating: null,
  } as any

  const emojiSrc = getCardCompatibilityIcon(mockRestaurant)
  const backgroundColor = getCompatibilityColor(mockRestaurant)

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        bg="var(--card-bg)"
        borderRadius="var(--r-card)"
        mx={4}
        maxH="85vh"
        pt={{ base: 'calc(env(safe-area-inset-top))', lg: 0 }}
      >
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Box bg="pink.100" borderRadius="var(--r-chip)" p={2}>
              <Users size={20} color="var(--chakra-colors-pink-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="var(--card-text)">
                TasteMatch™
              </Heading>
              {memberName && (
                <Text fontSize="sm" color="var(--card-text-2)">
                  Your TasteMatch with {memberName}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <VStack spacing={5} align="stretch">
            {/* Compatibility Score Banner */}
            {score != null && (
              <MotionBox
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Box
                  bg="linear-gradient(135deg, var(--chakra-colors-pink-500), var(--chakra-colors-purple-500))"
                  borderRadius="var(--r-card-compact)"
                  p={4}
                  position="relative"
                  overflow="hidden"
                >
                  <Box position="absolute" top={-20} right={-20} opacity={0.2}>
                    <Sparkles size={80} color="white" />
                  </Box>
                  <HStack spacing={3} position="relative" zIndex={1}>
                    <Box
                      bg={backgroundColor}
                      borderRadius="full"
                      p={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Image src={emojiSrc} alt="Match score" boxSize="32px" />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text color="white" fontWeight="bold" fontSize="md">
                        TasteMatch™
                      </Text>
                      <Text color="whiteAlpha.800" fontSize="xs">
                        Based on your shared preferences
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </MotionBox>
            )}

            {/* How it works */}
            <Box>
              <Heading size="sm" color="var(--card-text)" mb={3}>
                How It Works
              </Heading>
              <Text fontSize="sm" color="var(--card-text-2)" mb={4}>
                We compare your PersonalTaste™ Profiles to find how well your taste preferences align.
              </Text>

              <VStack spacing={3} align="stretch">
                {concepts.map((concept, index) => (
                  <MotionBox
                    key={concept.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                  >
                    <HStack
                      bg="var(--card-surface-2)"
                      borderRadius="var(--r-chip)"
                      p={3}
                      spacing={3}
                      borderLeft="3px solid"
                      borderLeftColor={concept.color}
                    >
                      <Box color={concept.color} flexShrink={0}>
                        {concept.icon}
                      </Box>
                      <VStack align="start" spacing={0} flex={1}>
                        <HStack spacing={0}>
                          <Text fontSize="sm" fontWeight="semibold" color="var(--card-text)">
                            {concept.title}
                          </Text>
                          {concept.trademark && (
                            <Text fontSize="xs" color="var(--card-text-3)">
                              {concept.trademark}
                            </Text>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="var(--card-text-2)">
                          {concept.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
